/**
 * Avatar uploads, on the streams-api image service.
 *
 * Follows what streams-app does for destination poster images (see its ImageService.js /
 * ImageUploadModal): sign, PUT the bytes straight to storage, mark the image uploaded. The
 * console never proxies the file, and nothing new had to be built server-side.
 *
 * Images are normalised to a square PNG before they leave the browser, so storage keeps one
 * predictable size and a 4MB phone photo does not become an app avatar.
 */

import { createImageUploadUrl, deleteImage, markImageUploaded } from './api.ts';

export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
export const AVATAR_EDGE = 512;
export const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

/**
 * The only assetType streams-app sends to this endpoint. Swap it for a dedicated value
 * (e.g. `app_avatar`) once the API recognises one — an unknown type is rejected.
 */
export const AVATAR_ASSET_TYPE = 'caption_img';

/** Problems worth showing to the person verbatim. */
export class UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadError';
  }
}

export interface UploadedAvatar {
  /** Needed to delete the image again if it is replaced, removed, or fails to persist. */
  imageId: string;
  url: string;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new UploadError('That file could not be read as an image.'));
    };
    image.src = objectUrl;
  });
}

/**
 * Centre-crops to a square and scales down to at most AVATAR_EDGE. Images already smaller
 * are not upscaled — a 64px logo stays crisp instead of being blurred up.
 */
export async function normaliseAvatar(file: File): Promise<Blob> {
  if (!AVATAR_TYPES.includes(file.type)) {
    throw new UploadError('Use a PNG, JPEG, WebP or GIF image.');
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new UploadError(
      `That image is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is ` +
        `${AVATAR_MAX_BYTES / 1024 / 1024}MB.`,
    );
  }

  const image = await loadImage(file);
  const source = Math.min(image.naturalWidth, image.naturalHeight);
  if (!source) throw new UploadError('That image appears to be empty.');

  const edge = Math.min(source, AVATAR_EDGE);
  const canvas = document.createElement('canvas');
  canvas.width = edge;
  canvas.height = edge;

  const context = canvas.getContext('2d');
  if (!context) throw new UploadError('This browser cannot process the image.');

  context.drawImage(
    image,
    (image.naturalWidth - source) / 2,
    (image.naturalHeight - source) / 2,
    source,
    source,
    0,
    0,
    edge,
    edge,
  );

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new UploadError('The image could not be processed.');
  return blob;
}

/**
 * PUTs the bytes to the signed URL. XHR rather than fetch purely for upload progress —
 * there is still no way to observe request progress with fetch. No Authorization header:
 * the signature is in the URL, and sending one breaks the storage request.
 */
function putBytes(
  uploadUrl: string,
  blob: Blob,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl, true);
    request.setRequestHeader('Content-Type', 'image/png');

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    });
    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new UploadError(`Storage rejected the upload (${request.status}).`));
    });
    request.addEventListener('error', () =>
      reject(new UploadError('The upload failed. Check your connection and try again.')),
    );
    request.addEventListener('abort', () => reject(new UploadError('Upload cancelled.')));

    request.send(blob);
  });
}

/** Best-effort cleanup of an image that was uploaded but never saved onto the app. */
export async function discardUpload(imageId: string): Promise<void> {
  try {
    await deleteImage(imageId);
  } catch {
    /* an orphaned image is not worth interrupting the person over */
  }
}

/**
 * The signed URL and the public one are the same object; the query string is only the
 * signature. `GET /images/{id}/download_url` hands back another *signed* URL, which would
 * expire out from under a saved avatar — so the readable URL is derived here instead.
 */
export const publicUrlFor = (uploadUrl: string) => uploadUrl.split('?')[0] ?? uploadUrl;

/**
 * normalise → sign → PUT → mark uploaded.
 * Returns the URL; writing it onto the app is the caller's job.
 */
export async function uploadAvatar(
  file: File,
  onProgress: (fraction: number) => void = () => {},
): Promise<UploadedAvatar> {
  const blob = await normaliseAvatar(file);

  const target = await createImageUploadUrl({
    assetType: AVATAR_ASSET_TYPE,
    type: 'image/png',
    size: blob.size,
    originalFilename: file.name || 'avatar.png',
  });

  if (!target?.uploadUrl || !target?.uploadId) {
    throw new UploadError('The image service did not return an upload target.');
  }

  onProgress(0);
  await putBytes(target.uploadUrl, blob, onProgress);
  onProgress(1);

  // Commits the record. Skipping this leaves the image in limbo.
  await markImageUploaded(target.uploadId);

  return { imageId: target.uploadId, url: publicUrlFor(target.uploadUrl) };
}
